import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { useFieldToolsStore } from "../stores/fieldToolsStore";
import {
  defaultSection180Options,
  downloadSection180,
  section180Figures,
  statesOf,
  type Section180FieldInput,
  type Section180Figure,
  type Section180Options,
} from "../features/section180/section180";
import { assembleSection180Inputs } from "../features/section180/section180Assemble";
import { Section180CustomizeModal } from "../features/section180/Section180CustomizeModal";
import { Section180ExcessList } from "../features/section180/Section180ExcessList";
import { Section180Note } from "../features/section180/Section180Note";
import { useUiMode, showsAdvancedControls } from "../stores/uiModeStore";
import {
  availableNutrients,
  type OfferedNutrient,
} from "../features/section180/section180Nutrients";
import { hasStatePricing, unpricedStates } from "../lib/statePrices";
import { stateOfPoint } from "../lib/usStates";
import { effectiveBranding, type Branding } from "../lib/branding";
import { Section180PanelHeader } from "../features/section180/Section180PanelHeader";
import { Section180ReportChoices } from "../features/section180/Section180ReportChoices";
import { useBranding } from "../features/branding/useBranding";
import { useOrgStore } from "../stores/orgStore";
import type { DashboardField } from "../types/contract";
import { MapSoilPanel } from "./MapSoilDropdown";
import { BuildRxPanel } from "./BuildRxDropdown";
import { SpreadFertilizerPanel } from "./SpreadFertilizerPanel";
import { CombineFieldsModal } from "../features/combine/CombineFieldsModal";
import { DeleteFieldsConfirm } from "../features/delete-fields/DeleteFieldsConfirm";
import { errMsg, type BatchRunner } from "./FieldActionData";

export type { BatchRunner };

// Top action bar: the "Map / Map soil properties / Fertility recs" actions run on ALL SELECTED
// FIELDS.

// SPLIT: the buttons moved to the global header, the PANELS did not.

// The header pieces are exported SEPARATELY because they no longer sit together: Map Soil / Build
// Rx / the count are centred in the header, while Visualize and Delete live in the grouped menu
// beside the logo. One component can't be in two places, so the shared `action` state lives in the
// store and each piece reads it.

import { FieldActionCenter, VisualizeButton } from "./FieldActionCenter";

export { FieldActionCenter, VisualizeButton };

export function Section180Panel({
  selected,
  onClose,
}: {
  selected: DashboardField[];
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const uiMode = useUiMode((s) => s.mode);
  const [note, setNote] = useState<string | null>(null);
  const activeOrgId = useOrgStore((st) => st.activeOrgId);
  const brandingQuery = useBranding(activeOrgId);
  // Settled means this org's fetch has landed. While a switch is in flight the previous
  // branding stays on the card, and the "not set up" note stays hidden.
  const brandingSettled =
    brandingQuery.isSuccess &&
    !brandingQuery.isFetching &&
    !brandingQuery.isPlaceholderData;
  const branding = brandingSettled
    ? brandingQuery.data
      ? effectiveBranding(brandingQuery.data)
      : null
    : brandingQuery.isPlaceholderData && brandingQuery.data
      ? effectiveBranding(brandingQuery.data)
      : null;
  const brandingLoaded = brandingSettled;
  // the STATES the selection spans — the customize pop-up needs one price row per state, and the
  // lookup is the same spatial one the report itself uses (resolved up front so the modal opens instantly)
  const [custStates, setCustStates] = useState<string[]>([]);
  const [customizing, setCustomizing] = useState(false);
  // the last CONFIRMED customization, kept so reopening Customize restores what the user typed
  // instead of making them retype a whole per-state price table.
  const [customOptions, setCustomOptions] = useState<Section180Options | null>(
    null,
  );
  const [offered, setOffered] = useState<OfferedNutrient[]>([]);
  // They feed BOTH the per-field Excess Fertility list below the presets AND the download, so the
  // panel can never show a figure the workbook would contradict, and surfacing it costs no extra
  // requests. The promise is held in a ref so a Download clicked while it is still in flight AWAITS
  // it rather than re-fetching.
  const prepRef = useRef<Promise<{
    inputs: Section180FieldInput[];
    lacking: string[];
  }> | null>(null);
  const [prep, setPrep] = useState<{
    inputs: Section180FieldInput[];
    lacking: string[];
  } | null>(null);
  const [prepping, setPrepping] = useState(false);
  const ensurePrep = () => {
    if (!prepRef.current) prepRef.current = assembleSection180Inputs(selected);
    return prepRef.current;
  };
  // `ensurePrep` closes over the current selection and is a new function every
  // render. The effect already re-runs when `selected` changes.
  const ensurePrepRef = useRef(ensurePrep);
  useLayoutEffect(() => {
    ensurePrepRef.current = ensurePrep;
  });
  useEffect(() => {
    let alive = true;
    prepRef.current = null; // a new selection invalidates the cache
    setPrep(null);
    setPrepping(true);
    ensurePrepRef
      .current()
      .then((p) => {
        if (alive) setPrep(p);
      })
      // a failure here is NOT surfaced as a note: the list simply stays quiet and Download reports
      // the error itself, exactly as it did before this panel previewed anything.
      .catch(() => {
        if (alive) setPrep(null);
      })
      .finally(() => {
        if (alive) setPrepping(false);
      });
    return () => {
      alive = false;
    };
  }, [selected]);
  // The list is a preview of the ROGO methodology so it never follows a Branded customization — a
  // customized download can legitimately differ, and the caption under the heading says which
  // market these are. The figure is the one the server will print.
  const [rogoFigures, setRogoFigures] = useState<Section180Figure[] | null>(
    null,
  );
  const [figuresReady, setFiguresReady] = useState(false);
  useEffect(() => {
    if (!prep?.inputs.length) {
      setRogoFigures(null);
      setFiguresReady(false);
      return;
    }
    let alive = true;
    setFiguresReady(false);
    section180Figures(
      prep.inputs,
      defaultSection180Options(statesOf(prep.inputs)),
    )
      .then((figures) => {
        if (alive) setRogoFigures(figures);
      })
      .catch(() => {
        if (alive) setRogoFigures(null);
      })
      .finally(() => {
        if (alive) setFiguresReady(true);
      });
    return () => {
      alive = false;
    };
  }, [prep]);
  // One row per SELECTED field, in selection order — a field that cannot produce an honest figure
  // says WHY instead of showing a number. Never fall back to the national market for an unpriced
  // state: that is precisely what the ROGO Default card refuses to do.
  const excessRows = useMemo(
    () =>
      selected.map((f) => {
        const idx = prep
          ? prep.inputs.findIndex((i) => i.field.field_id === f.field_id)
          : -1;
        if (!prep) return { id: f.field_id, name: f.field_name, reason: null };
        if (idx < 0)
          return {
            id: f.field_id,
            name: f.field_name,
            reason: "Run Map Soil first",
          };
        const st = prep.inputs[idx]!.field.state;
        if (!hasStatePricing(st)) {
          return {
            id: f.field_id,
            name: f.field_name,
            reason:
              st === "NA"
                ? "No published market — outside the 50 states"
                : `No published ROGO market for ${st}`,
          };
        }
        if (!figuresReady)
          return { id: f.field_id, name: f.field_name, reason: null };
        const v = rogoFigures?.[idx]?.excess.total_value;
        return v == null
          ? { id: f.field_id, name: f.field_name, reason: "Unavailable" }
          : { id: f.field_id, name: f.field_name, value: v };
      }),
    [selected, prep, rogoFigures, figuresReady],
  );
  useEffect(() => {
    let alive = true;
    void Promise.all(
      selected.map((f) => stateOfPoint(f.centroid[0], f.centroid[1])),
    ).then((sts) => {
      if (!alive) return;
      const uniq: string[] = [];
      for (const st of sts) {
        const key = st ?? "NA";
        if (!uniq.includes(key)) uniq.push(key);
      }
      setCustStates(uniq.length ? uniq : ["NA"]);
    });
    void Promise.all(
      selected.map((f) => api.fetchSoilSamples(f.field_id).catch(() => [])),
    ).then((sets) => {
      if (alive) setOffered(availableNutrients(sets));
    });
    return () => {
      alive = false;
    };
  }, [selected]);
  const run = async (
    useBranding: Branding | null,
    options?: Section180Options,
  ) => {
    if (!selected.length || busy) return;
    setCustomizing(false);
    setBusy(true);
    setNote(null);
    try {
      // the panel already assembled this when it opened; reuse it rather than re-fetching
      const { inputs, lacking } = await ensurePrep();
      if (lacking.length) {
        setNote(
          selected.length === 1
            ? "File Section 180 needs the soil-property maps first. Run Map Soil on this field, then come back."
            : `File Section 180 needs the soil-property maps first. Run Map Soil on ${lacking.join(", ")}, then come back.`,
        );
        return;
      }
      await downloadSection180(
        inputs,
        inputs.length === 1 ? inputs[0]!.field.field_name : null,
        useBranding,
        options,
      );
    } catch (e) {
      setNote(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  // The ROGO Default is UNAVAILABLE for a selection touching any state the published price table
  // cannot price.
  const unpriced = custStates.length ? unpricedStates(custStates) : [];
  const rogoBlocked = unpriced.length > 0;
  return (
    <div className="mt-2 flex flex-col overflow-hidden rounded-lg border border-line bg-surface text-fg shadow-md">
      <Section180PanelHeader
        selectedCount={selected.length}
        singleFieldName={selected[0]?.field_name}
        busy={busy}
        onClose={onClose}
      />

      <Section180ReportChoices
        selectedCount={selected.length}
        busy={busy}
        rogoBlocked={rogoBlocked}
        unpriced={unpriced}
        branding={branding}
        brandingLoaded={brandingLoaded}
        showCustomize={showsAdvancedControls(uiMode)}
        customizeDisabled={
          selected.length === 0 ||
          busy ||
          branding == null ||
          custStates.length === 0
        }
        onRogo={() => void run(null)}
        onBranded={() => void run(branding)}
        onCustomize={() => setCustomizing(true)}
      />

      {/* Nothing is recomputed here, so the panel and the workbook cannot disagree. */}
      <Section180ExcessList
        prepping={prepping}
        showReading={prepping && prep == null}
        rows={excessRows}
      />

      {customizing && (
        <Section180CustomizeModal
          states={custStates}
          available={offered}
          initial={customOptions ?? undefined}
          onCancel={() => setCustomizing(false)}
          onConfirm={(options) => {
            setCustomOptions(options); // survives the download so a re-open restores the edits
            void run(branding, options);
          }}
        />
      )}
      {note != null && (
        <Section180Note note={note} onDismiss={() => setNote(null)} />
      )}
    </div>
  );
}

export { CombineFieldsButton, DeleteFieldsButton } from "./FieldMenuButtons";

export { FieldActionsRow } from "./FieldActionsRow";

/* * Content-area half: the inline panels the header buttons open, plus the delete confirmation. */
export function FieldActionPanels({
  selected,
}: {
  selected: DashboardField[];
}) {
  const action = useFieldToolsStore((s) => s.action);
  const closeAction = useFieldToolsStore((s) => s.closeAction);
  const confirmDelete = useFieldToolsStore((s) => s.confirmDelete);
  const confirmCombine = useFieldToolsStore((s) => s.confirmCombine);
  const setConfirmCombine = useFieldToolsStore((s) => s.setConfirmCombine);
  const setConfirmDelete = useFieldToolsStore((s) => s.setConfirmDelete);
  const n = selected.length;
  if (n === 0) return null;
  return (
    <>
      {confirmDelete && (
        <DeleteFieldsConfirm
          fields={selected}
          onClose={() => setConfirmDelete(false)}
        />
      )}
      {confirmCombine && selected.length > 1 && (
        <CombineFieldsModal
          fields={selected}
          onClose={() => setConfirmCombine(false)}
        />
      )}
      {action === "soilmap" && (
        <MapSoilPanel fields={selected} onClose={closeAction} />
      )}
      {action === "fert" && (
        <BuildRxPanel fields={selected} onClose={closeAction} />
      )}
      {action === "spread" && (
        <SpreadFertilizerPanel fields={selected} onClose={closeAction} />
      )}
      {action === "section180" && (
        <Section180Panel selected={selected} onClose={closeAction} />
      )}
    </>
  );
}

export { MapAddFieldDataButton } from "./MapAddFieldDataButton";
